import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';
import { SegmentControl } from '@/components/ui/segment-control';
import { Users, Copy, ChevronDown, Gift, Loader2, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getWebApp, haptic } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { PartnerSubscription, SubscriptionKindFilter, SubscriptionStatusFilter } from '@/api/types';
import { channelLabel } from '@/lib/channel';
import { ClientsSwitch } from '@/components/SectionSwitch';

type SubsSearch = { client?: string; clientName?: string };

export const Route = createFileRoute('/subscriptions')({
  component: Subscriptions,
  // Переход из «Клиентов»: подписки одного человека. Имя едет рядом, чтобы плашка
  // фильтра не ждала отдельного запроса.
  validateSearch: (s: Record<string, unknown>): SubsSearch => ({
    ...(typeof s.client === 'string' && s.client ? { client: s.client } : {}),
    ...(typeof s.clientName === 'string' && s.clientName ? { clientName: s.clientName } : {}),
  }),
});

type Filter = SubscriptionStatusFilter;
type Kind = Exclude<SubscriptionKindFilter, 'all'>;

/** Задержка перед отправкой поискового запроса: не дёргаем бэк на каждую букву. */
const SEARCH_DEBOUNCE_MS = 300;

function Subscriptions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client: clientId, clientName } = Route.useSearch();
  const [filter, setFilter] = useState<Filter>('all');
  // Платные vs триал — отдельный срез, чтобы платящие клиенты не мешались с
  // халявщиками-триальщиками. Дефолт «Платные» (главный запрос партнёров).
  const [kind, setKind] = useState<Kind>('paid');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  // Фильтрация и пагинация — на бэке. Раньше страница фильтровала первые 100 записей
  // локально, из-за чего у партнёров с большой базой старые подписки не отображались.
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['partner-subs', filter, kind, debouncedSearch, clientId],
    queryFn: ({ pageParam }) =>
      api.subscriptions({
        status: filter,
        kind,
        q: debouncedSearch,
        clientId,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const { data: counts } = useQuery({
    // kind в ключе: статусные счётчики считаются в пределах выбранной вкладки вида,
    // иначе на «Платных» бейдж «Все» включал бы ещё и пробные.
    queryKey: ['partner-subs-counts', debouncedSearch, kind, clientId],
    queryFn: () => api.subscriptionCounts({ q: debouncedSearch, kind, clientId }),
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    tg.MainButton.hide();
    const onBack = () => navigate({ to: '/clients' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  const filtered = data?.pages.flatMap((p) => p.items) ?? [];
  // Счётчик в подписи вкладки — из /counts (вся база), а не из загруженных страниц.
  const withCount = (label: string, n: number | undefined) => (n === undefined ? label : `${label} ${n}`);

  return (
    <div className="p-4 space-y-4 pb-28">
      <ClientsSwitch />
      <div className="px-1 pt-2">
        <h2 className="font-display text-2xl font-bold tracking-tight mb-3">{t('Subscriptions')}</h2>
        <div className="relative mb-2.5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dim pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск: @username, почта, tg id, ID подписки"
            className="w-full surface-inset rounded-full pl-9 pr-9 py-2.5 text-sm outline-none placeholder:text-dim"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-dim"
              aria-label="Очистить поиск"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {clientId ? (
          <div className="mb-2.5 flex items-center gap-2 text-[13px]">
            <span className="text-dim">Клиент:</span>
            <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-white/[0.07] py-1 pl-3 pr-1.5 text-primary-light">
              <span className="truncate">{clientName || 'клиент'}</span>
              <button
                type="button"
                aria-label="Показать всех"
                onClick={() => navigate({ to: '/subscriptions', search: {} })}
                className="flex h-5 w-5 items-center justify-center rounded-full"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          </div>
        ) : null}
        <div className="mb-2.5">
          <SegmentControl<Kind>
            value={kind}
            onChange={setKind}
            options={[
              { value: 'paid', label: withCount('Платные', counts?.paid) },
              { value: 'trial', label: withCount('Триал', counts?.trial) },
              // Спецтариф «тест белых списков»: платный, но маржа партнёра нулевая.
              // Раньше лежал среди «Платных» и путал цифры продаж.
              { value: 'special', label: withCount('Тест', counts?.special) },
            ]}
          />
        </div>
        <SegmentControl<Filter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: withCount(t('Subs filter all'), counts?.total) },
            { value: 'active', label: withCount(t('Subs filter active'), counts?.active) },
            { value: 'expired', label: withCount(t('Subs filter expired'), counts?.expired) },
            { value: 'frozen', label: withCount(t('Subs filter frozen'), counts?.frozen) },
          ]}
        />
      </div>

      <div className="space-y-[11px]">
        {isPending ? (
          <SubsSkeleton />
        ) : filtered.length === 0 ? (
          <div className="surface-card rounded-[22px] py-10 px-5 text-center">
            <div className="w-[52px] h-[52px] rounded-[16px] bg-white/[0.05] flex items-center justify-center mx-auto mb-3.5 text-[#6b6d75]">
              <Users className="w-[26px] h-[26px]" strokeWidth={1.6} />
            </div>
            <div className="font-display font-semibold text-[15px] text-muted-foreground">{t('Empty subs')}</div>
            <div className="text-[12.5px] text-dim mt-1.5">{t('Empty subs desc')}</div>
          </div>
        ) : (
          filtered.map((sub) => (
            <SubCard
              key={sub.id}
              sub={sub}
              expanded={expandedId === sub.id}
              onToggle={() => {
                haptic('light');
                setExpandedId((prev) => (prev === sub.id ? null : sub.id));
              }}
            />
          ))
        )}

        {hasNextPage && (
          <button
            type="button"
            onClick={() => {
              haptic('light');
              fetchNextPage();
            }}
            disabled={isFetchingNextPage}
            className="w-full surface-card rounded-[18px] py-3.5 text-[13.5px] font-semibold text-primary-light disabled:opacity-60"
          >
            {isFetchingNextPage ? (
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            ) : (
              'Показать ещё'
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function SubCard({
  sub,
  expanded,
  onToggle,
}: {
  sub: PartnerSubscription;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="surface-card rounded-[20px] p-[17px]">
      <button type="button" onClick={onToggle} className="w-full text-left">
        {/* Плашки переносятся под себя, а не съедают строку: с «Перенесена» и
            «Заморожена» на 320px от имени клиента оставалось 29px. */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-[110px] flex-1">
            <div className="font-bold text-[15px] truncate">{sub.clientName}</div>
            <div className="font-mono text-[11px] text-dim truncate mt-0.5">
              {sub.clientTgId ? `tg id ${sub.clientTgId}` : 'без Telegram'}
              {!sub.isMigrated && channelLabel(sub.channel) ? ` · ${channelLabel(sub.channel)}` : ''}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {sub.isTrial && (
              <span className="font-display text-[10.5px] font-semibold px-2.5 py-1 rounded-full bg-[rgba(148,163,184,0.16)] text-[#94a3b8]">
                TRIAL
              </span>
            )}
            {sub.isSpecial && (
              <span className="font-display text-[10.5px] font-semibold px-2.5 py-1 rounded-full bg-[rgba(148,163,184,0.16)] text-[#94a3b8]">
                ТЕСТ
              </span>
            )}
            {/* Переехала с другой франшизы: у нас за неё не платили, и «+0» в колонке
                дохода читалось бы как продажа даром. Цену у такой строки не рисуем. */}
            {sub.isMigrated && (
              <span className="font-display text-[10.5px] font-semibold px-2.5 py-1 rounded-full bg-white/[0.07] text-primary-light">
                Перенесена
              </span>
            )}
            <StatusBadge status={sub.status} />
            <ChevronDown
              className={`w-4 h-4 text-dim transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
        <div className="grid grid-cols-[1.4fr_1fr_0.8fr] gap-2 mt-4 pt-3.5 border-t border-white/[0.06]">
          <div>
            <div className="eyebrow">{t('Plan')}</div>
            <div className="font-bold text-[13.5px] mt-1">{sub.planName}</div>
          </div>
          {sub.isLifetime ? (
            // У «Навсегда» срока нет: `daysLeft` до 2100 года, и число там было бы
            // бессмыслицей. Отключённая подписка без срока просто не действует.
            <div>
              <div className="eyebrow">Срок</div>
              <div className="font-display font-bold text-sm mt-1">
                {sub.status === 'expired' ? 'не действует' : 'навсегда'}
              </div>
            </div>
          ) : (
            <div>
              <div className="eyebrow">{t('Expires')}</div>
              <div className="font-display font-bold text-sm nums mt-1">
                {sub.daysLeft} {t('Days')}
              </div>
            </div>
          )}
          {sub.isMigrated ? (
            <div />
          ) : (
            <div className="text-right">
              <div className="eyebrow">+₽</div>
              {/* Знак по значению: скидка сверх маржи даёт минус, и выходило «+-1». */}
              <div
                className={`font-display font-bold text-sm nums mt-1 ${
                  sub.partnerEarnedRub < 0 ? 'text-[#f87171]' : 'text-money-light'
                }`}
              >
                {sub.partnerEarnedRub < 0 ? '−' : '+'}
                {Math.abs(sub.partnerEarnedRub).toLocaleString('ru')}
              </div>
            </div>
          )}
        </div>
      </button>
      {expanded && <SubDetail subId={sub.id} />}
    </div>
  );
}

const toGb = (bytes: number) => bytes / 1024 / 1024 / 1024;

function SubDetail({ subId }: { subId: string }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['partner-sub-detail', subId],
    queryFn: () => api.subscriptionDetail(subId),
    // Live-трафик кешируется на бэке 60с — не дёргаем AG лишний раз.
    staleTime: 60_000,
  });

  if (isPending) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="w-4 h-4 animate-spin text-primary-light" />
      </div>
    );
  }
  if (isError || !data) {
    return <div className="text-[12px] text-dim pt-3">Не удалось загрузить деталь</div>;
  }

  const fmtDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  let trafficLabel: string;
  if (data.usedTrafficBytes === null) {
    trafficLabel = data.trafficLimitBytes !== null ? `— / ${Math.floor(toGb(data.trafficLimitBytes))} ГБ` : '—';
  } else if (data.trafficLimitBytes === null) {
    trafficLabel = `${toGb(data.usedTrafficBytes).toFixed(2)} ГБ · безлимит`;
  } else {
    const leftGb = Math.max(0, toGb(data.trafficLimitBytes - data.usedTrafficBytes));
    trafficLabel = `${toGb(data.usedTrafficBytes).toFixed(2)} / ${Math.floor(toGb(data.trafficLimitBytes))} ГБ · осталось ${leftGb.toFixed(1)}`;
  }

  // Показываем и копируем НАШ id подписки — тот же, что человек видит в подвале
  // страницы ключа. Раньше здесь был uuid AdaptGroup, и партнёр, сверяя два экрана,
  // видел разные строки и решал, что база разъехалась.
  const copyUuid = () => {
    navigator.clipboard?.writeText(data.id).then(
      () => {
        haptic('success');
        toast.success('UUID скопирован');
      },
      () => toast.error('Не удалось скопировать'),
    );
  };

  // У FranCenter («Навсегда» и её пробник) лимит стоит только на серверах обхода и
  // сбрасывается 1-го числа, обычные серверы без лимита.
  const scvpn = data.supplier === 'scvpn';

  return (
    <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-2.5">
      <div>
        <div className="eyebrow">{scvpn ? 'Обход белых списков' : 'Трафик (белые списки)'}</div>
        <div className="font-display font-bold text-[13px] nums mt-1">{trafficLabel}</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="eyebrow">{data.isMigrated ? 'Перенесена' : 'Куплена'}</div>
          <div className="text-[12.5px] font-semibold mt-1">{fmtDate(data.createdAt)}</div>
        </div>
        {/* Дата окончания у «Навсегда» 2100 год: выводить её нельзя, а срок словом
            уже стоит в строке карточки над деталью. */}
        {data.isLifetime ? null : (
          <div>
            {/* У замороженной подписки end_date стоит на месте (AdaptGroup сдвинет её
                только при разморозке): дату показывать нельзя, показываем остаток. */}
            <div className="eyebrow">{data.status === 'frozen' ? 'Осталось' : 'Действует до'}</div>
            <div className="text-[12.5px] font-semibold mt-1">
              {data.status === 'frozen' ? `${data.daysLeft} дн. (на паузе)` : fmtDate(data.expiresAt)}
            </div>
          </div>
        )}
      </div>
      {/* Подарить дни можно только подписке AdaptGroup: у «Навсегда» срока нет (бэк
          отвечает 409), а дни дарятся продлением у AdaptGroup, которого у подписок
          FranCenter нет. */}
      <GiftDaysBlock
        subscriptionId={data.id}
        disabled={data.status === 'frozen' || data.isTrial || data.isLifetime || scvpn}
      />
      <div>
        <div className="eyebrow">ID подписки</div>
        <button
          type="button"
          onClick={copyUuid}
          className="mt-1 flex items-center gap-1.5 max-w-full text-left"
          title="Скопировать ID"
        >
          <Copy className="w-3.5 h-3.5 text-primary-light shrink-0" />
          <span className="font-mono text-[11px] text-muted-foreground truncate">{data.id}</span>
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'active' | 'expired' | 'frozen' }) {
  const cfg = {
    active: { cls: 'bg-[rgba(56,224,165,0.13)] text-money-light', label: 'Активна' },
    expired: { cls: 'bg-[rgba(248,113,113,0.14)] text-danger', label: 'Истекла' },
    frozen: { cls: 'bg-white/[0.07] text-primary-light', label: 'Заморожена' },
  }[status];
  return (
    <span className={`flex items-center gap-1.5 font-display text-[10.5px] font-semibold px-2.5 py-1 rounded-full shrink-0 ${cfg.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {cfg.label}
    </span>
  );
}

/** Карточка подписки: имя клиента с tg id, бейдж статуса, строка тариф/срок/доход. */
function SubsSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <SkeletonCard key={i} className="rounded-[20px] p-[17px]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-2">
              <SkeletonLine size="lg" className="w-2/5" />
              <SkeletonLine size="xs" className="w-1/3" />
            </div>
            <Skeleton className="h-[22px] w-20 rounded-full shrink-0" />
          </div>
          <div className="grid grid-cols-[1.4fr_1fr_0.8fr] gap-2 mt-4 pt-3.5 border-t border-white/[0.06]">
            {[0, 1, 2].map((c) => (
              <div key={c} className="space-y-1.5">
                <SkeletonLine size="xs" className="w-12" />
                <SkeletonLine className="w-16" />
              </div>
            ))}
          </div>
        </SkeletonCard>
      ))}
    </>
  );
}


/** Подарить клиенту дни за свой счёт.
 *
 * С партнёра списывается ЗАКУПОЧНАЯ цена, а не розничная: столько же ему стоят приз
 * розыгрыша и пачка ваучеров. Клиент не платит ничего.
 */
function GiftDaysBlock({ subscriptionId, disabled }: { subscriptionId: string; disabled: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(30);
  const { data: quote, isPending: quotePending } = useQuery({
    queryKey: ['gift-quote', subscriptionId, days],
    queryFn: () => api.giftQuote(subscriptionId, days),
    enabled: open && !disabled,
  });
  // Ключ одного нажатия: двойной тап и повтор после обрыва связи не списывают дважды.
  const [requestId, setRequestId] = useState(newRequestId);
  const gift = useMutation({
    mutationFn: () => api.giftDays(subscriptionId, days, true, requestId),
    onSuccess: (r) => {
      haptic('success');
      toast.success(`Подарено ${r.days} дн.`);
      setRequestId(newRequestId());
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['partner-subscription', subscriptionId] });
      qc.invalidateQueries({ queryKey: ['partner-me'] });
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не удалось подарить дни');
    },
  });

  if (disabled) return null;
  return (
    <div className="surface-inset rounded-[14px] p-3">
      {!open ? (
        <button
          type="button"
          onClick={() => {
            haptic('light');
            setOpen(true);
          }}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-[11px] text-[13.5px] font-bold bg-white/[0.07] active:scale-[0.98] transition-transform"
        >
          <Gift className="w-4 h-4" />
          Подарить дни
        </button>
      ) : (
        <div className="space-y-2.5">
          <div className="text-[12.5px] font-semibold">Подарить дни за свой счёт</div>
          <p className="text-[11px] text-dim leading-relaxed">
            Клиент ничего не платит. С вас спишется закупочная цена этих дней.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {[7, 14, 30, 60, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`px-2.5 py-1.5 rounded-full text-[12px] font-semibold ${
                  days === d ? 'bg-white/[0.12] text-foreground' : 'bg-white/[0.06] text-muted-foreground'
                }`}
              >
                {d} дн.
              </button>
            ))}
          </div>
          <div className="text-[12px]">
            {quotePending ? (
              <span className="text-dim">Считаем цену…</span>
            ) : quote ? (
              <>
                Спишется{' '}
                <span className="nums font-bold text-money-light">
                  ${(quote.cost_usd_minor / 100).toFixed(2)}
                </span>
                , доступно ${(quote.spendable_usd_minor / 100).toFixed(2)}
                {!quote.enough && <div className="text-danger mt-1">Не хватает средств</div>}
              </>
            ) : (
              <span className="text-danger">Не удалось посчитать цену</span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex-1 py-2.5 rounded-[11px] text-[13px] font-bold bg-white/[0.06]"
            >
              Отмена
            </button>
            <button
              type="button"
              disabled={gift.isPending || !quote?.enough}
              onClick={() => gift.mutate()}
              className="flex-1 py-2.5 rounded-[11px] text-[13px] font-bold bg-primary text-primary-foreground disabled:opacity-40"
            >
              {gift.isPending ? 'Дарим…' : 'Подарить'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function newRequestId(): string {
  const b = new Uint8Array(12);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}
